import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { formatDateYMD, isDateString } from '../editor/types';

type Props = {
  /** YYYY-MM-DD 或空串 */
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  /** 输入框 class（填写端 'input'，预览端 'pq-input'） */
  className?: string;
  style?: React.CSSProperties;
  ariaLabel?: string;
  disabled?: boolean;
};

const WEEKDAYS = ['日', '一', '二', '三', '四', '五', '六'];
const PANEL_W = 292;
const PANEL_H = 344;

function parseYMD(s: string): Date {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

/**
 * 0907 日期校验专用输入：文本框左侧日历图标；点击输入框 / 图标弹出日历面板（年 / 月切换箭头 + 日期格子），
 * 底部「清空」「现在」；点某天即选中并关闭。值固定 YYYY-MM-DD，输入框只读（避免手机端同时弹键盘）。
 * 面板用 portal 挂到 body 并 fixed 定位，不会被表格 / overflow 容器裁掉。
 */
export default function DatePickerInput({ value, onChange, placeholder = '请选择日期', className = 'input', style, ariaLabel, disabled }: Props) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const selected = isDateString(value) ? parseYMD(value) : null;
  const [view, setView] = useState<{ y: number; m: number }>(() => {
    const base = selected ?? new Date();
    return { y: base.getFullYear(), m: base.getMonth() };
  });

  const openPanel = () => {
    if (disabled) return;
    const base = isDateString(value) ? parseYMD(value) : new Date();
    setView({ y: base.getFullYear(), m: base.getMonth() });
    setOpen(true);
  };

  // 定位到输入框下方；下方放不下且上方放得下时翻到上方；跟随滚动 / 窗口变化
  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const el = wrapRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const left = Math.max(8, Math.min(r.left, window.innerWidth - PANEL_W - 8));
      let top = r.bottom + 4;
      if (top + PANEL_H > window.innerHeight - 8 && r.top - PANEL_H - 4 > 8) top = r.top - PANEL_H - 4;
      setPos({ top, left });
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open]);

  // 点外面 / Esc 关闭
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent | TouchEvent) => {
      const t = e.target as Node;
      if (wrapRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown, { passive: true });
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const pick = (d: Date) => {
    onChange(formatDateYMD(d));
    setOpen(false);
  };
  const shiftMonth = (delta: number) => {
    setView((v) => {
      const d = new Date(v.y, v.m + delta, 1);
      return { y: d.getFullYear(), m: d.getMonth() };
    });
  };
  const shiftYear = (delta: number) => setView((v) => ({ y: v.y + delta, m: v.m }));

  // 6 行 × 7 列：从本月 1 号所在周的周日开始
  const today = new Date();
  const first = new Date(view.y, view.m, 1);
  const start = new Date(view.y, view.m, 1 - first.getDay());
  const cells: Date[] = Array.from({ length: 42 }, (_, i) => new Date(start.getFullYear(), start.getMonth(), start.getDate() + i));

  const panel = open && pos ? (
    <div className="date-panel" ref={panelRef} role="dialog" aria-label="选择日期" style={{ top: pos.top, left: pos.left, width: PANEL_W }}>
      <div className="date-panel-head">
        <button type="button" className="date-nav" onClick={() => shiftYear(-1)} title="上一年" aria-label="上一年">«</button>
        <button type="button" className="date-nav" onClick={() => shiftMonth(-1)} title="上个月" aria-label="上个月">‹</button>
        <span className="date-panel-title">{view.y} 年 {view.m + 1} 月</span>
        <button type="button" className="date-nav" onClick={() => shiftMonth(1)} title="下个月" aria-label="下个月">›</button>
        <button type="button" className="date-nav" onClick={() => shiftYear(1)} title="下一年" aria-label="下一年">»</button>
      </div>
      <div className="date-grid">
        {WEEKDAYS.map((w) => <span key={w} className="date-weekday">{w}</span>)}
        {cells.map((d) => {
          const outside = d.getMonth() !== view.m;
          const isToday = sameDay(d, today);
          const isSel = !!selected && sameDay(d, selected);
          return (
            <button
              type="button"
              key={d.toISOString()}
              className={`date-cell${outside ? ' is-outside' : ''}${isToday ? ' is-today' : ''}${isSel ? ' is-selected' : ''}`}
              onClick={() => pick(d)}
              aria-label={formatDateYMD(d)}
              aria-pressed={isSel}
            >
              {d.getDate()}
            </button>
          );
        })}
      </div>
      <div className="date-panel-foot">
        <button type="button" className="btn" onClick={() => { onChange(''); setOpen(false); }}>清空</button>
        <button type="button" className="btn btn-primary" onClick={() => pick(new Date())}>现在</button>
      </div>
    </div>
  ) : null;

  return (
    <div className="date-input" ref={wrapRef} style={style}>
      <button type="button" className="date-input-icon" onClick={openPanel} disabled={disabled} title="打开日历" aria-label="打开日历">
        <IconCalendar />
      </button>
      <input
        className={`${className} date-input-field`}
        value={value}
        placeholder={placeholder}
        readOnly
        disabled={disabled}
        aria-label={ariaLabel}
        onClick={openPanel}
        onFocus={openPanel}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openPanel(); }
          if (e.key === 'Backspace' || e.key === 'Delete') onChange('');
        }}
      />
      {panel && createPortal(panel, document.body)}
    </div>
  );
}

function IconCalendar() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M3 10h18M8 3v4M16 3v4" />
    </svg>
  );
}
